import { ToolNames } from '@kanbu/schema/contracts';
import { Agent, Role, type AgentType } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { useFieldState } from 'informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import {
  offeredBuiltinToolNames,
  type OfferedBuiltinToolName,
} from '@/features/chats/builtinToolOptions';
import { useAccess } from '@/hooks/useAccess';

export type SuperAdminFieldsProps = {};

export const SuperAdminFields = memo(
  function SuperAdminFields({}: SuperAdminFieldsProps) {
    const { t } = useLingui();
    const { hasAccess } = useAccess();
    const agentFieldState = useFieldState('agent');
    const agentTypeFieldState = useFieldState('agentType');

    const selectedAgent = agentFieldState.value as Agent | undefined;

    /**
     * Built from the partition in `builtinToolOptions.ts` rather than written
     * out here, so a built-in that nobody added to that partition fails its
     * test instead of quietly becoming unreachable in the admin UI. The label
     * record is exhaustive over the offered names, so a newly offered tool
     * without a label is a compile error.
     */
    const builtinToolLabels: Record<OfferedBuiltinToolName, string> = {
      [ToolNames.RecordAnswerSources]: t`Voice: Record answer sources (may increase latency)`,
      [ToolNames.DocumentSearch]: t`Document search`,
      [ToolNames.FindDocuments]: t`Find documents`,
      [ToolNames.GetDocumentOverview]: t`Document overview`,
      [ToolNames.FetchUrl]: t`Fetch URL`,
      [ToolNames.SeeImage]: t`See Image`,
      [ToolNames.ViewPage]: t`View PDF Page`,
      [ToolNames.SearchPdf]: t`Search PDF`,
      [ToolNames.GetOperator]: t`Operator handoff`,
      [ToolNames.UpdateWorkingMemory]: t`Working memory`,
      [ToolNames.GetShopProducts]: t`Shop: Product search`,
      [ToolNames.GetSimilarProducts]: t`Shop: Similar products`,
      [ToolNames.GetShopProductDetails]: t`Shop: Product details`,
      [ToolNames.GetShopCategories]: t`Shop: Categories`,
      [ToolNames.GetShopDeliveryOptions]: t`Shop: Delivery options`,
    };

    const builtinToolOptions = Object.fromEntries(
      offeredBuiltinToolNames(
        selectedAgent,
        agentTypeFieldState.value as AgentType | undefined,
      ).map(name => [name, builtinToolLabels[name as OfferedBuiltinToolName]]),
    );

    const { schema } = useFieldsSchema(
      [
        {
          name: 'agentConfig.systemPrompt',
          visible: hasAccess([Role.SuperAdmin]),
          label: t`System prompt`,
          type: 'text',
          uiProps: {
            rows: 10,
          },
          uiControl: 'textarea',
        },
        {
          $relevant: {
            $when: ({ formState }) => {
              const agent = (formState as TypedFormState<{ agent?: Agent }>)
                .values?.agent;

              return (
                agent === Agent.Conversational || agent === Agent.ShopAssistant
              );
            },
            $fields: [
              {
                name: 'agentConfig.enabledBuiltinTools',
                label: t`Enabled tools`,
                type: 'multiselect',
                visible: hasAccess([Role.SuperAdmin]),
                options: builtinToolOptions,
                tooltip: t`Built-in tools for this agent. When you change agent, the list resets to that agent's defaults.`,
                showOptional: false,
              },
            ],
          },
        },
      ],
      [t, hasAccess, selectedAgent, builtinToolOptions],
    );

    return (
      <Card title={t`Superadmin configuration`} variant='secondary'>
        <SchemaFields schema={schema} />
      </Card>
    );
  },
);
